/** A video on a page, narrowed to what structured data needs. */
export interface PageVideo {
    /** Link the video is played from. */
    url: string;
    /** Video title, when the professional set one. */
    title?: string;
    /** Thumbnail URL for the video. */
    thumbnailUrl?: string;
    /** Date the video was added, in whatever format the page builder stored. */
    dateCreated?: string;
}

/** Page-builder presets whose columns can hold a video. */
const VIDEO_PRESET_TYPES = new Set([
    'SingleVideoModule',
    'VideoParagraphModule',
]);

interface VideoBearingModule {
    presetType: string;
    columns: ReadonlyArray<{
        video?: {
            videoURL?: string | null;
            title?: string | null;
            coverImageThumbnail?: string | null;
            dateCreated?: string | null;
        } | null;
    }>;
}

/**
 * Pull every playable video out of a page's page-builder modules.
 *
 * Projects and articles are both built from the same module presets, so this is the single place that
 * knows which presets hold a video and how to read one out. Columns without a usable link are dropped:
 * a video module can exist in a draft state before the professional pastes a link.
 */
export const collectModuleVideos = (
    modules: ReadonlyArray<VideoBearingModule> | null | undefined,
): PageVideo[] =>
    (modules ?? [])
        .filter((module) => VIDEO_PRESET_TYPES.has(module.presetType))
        .flatMap((module) => module.columns)
        .flatMap((column) => {
            const url = column?.video?.videoURL?.trim();

            if (!url) {
                return [];
            }

            return [
                {
                    url,
                    title: column.video?.title ?? undefined,
                    thumbnailUrl:
                        column.video?.coverImageThumbnail ?? undefined,
                    dateCreated: column.video?.dateCreated ?? undefined,
                },
            ];
        });
